import { motion } from "motion/react";
import Footer from "../../components/footer/Footer";
import Navbar from "../../components/navbar/Navbar";
import "./Digitalrecongnization.css";
import React, { useRef, useState, useEffect } from "react";

function DigitalRecongnization() {
  const API_BASE_URL = "https://web-production-83e64.up.railway.app/";
  function DrawingCanvas() {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [result, setResult] = useState<{
      digit: number;
      confidence: number;
      probabilities: number[];
    } | null>(null);
    const [loading, setLoading] = useState(false);
    const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const context = canvas.getContext("2d");
      if (!context) return;

      // Initialize white background
      context.fillStyle = "white";
      context.fillRect(0, 0, canvas.width, canvas.height);

      // Set brush
      context.strokeStyle = "black";
      context.lineWidth = 15;
      context.lineCap = "round";
      context.lineJoin = "round";

      setCtx(context);
    }, []);

    const getCoordinates = (
      e:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();

      // Calculate scale factors
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      if ("touches" in e) {
        return {
          x: (e.touches[0].clientX - rect.left) * scaleX,
          y: (e.touches[0].clientY - rect.top) * scaleY,
        };
      }
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    };

    const startDrawing = (
      e:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>
    ) => {
      e.preventDefault();
      const coords = getCoordinates(e);
      if (!coords) return;
      const { x, y } = coords;
      if (!ctx) return;
      ctx.beginPath();
      ctx.moveTo(x, y);
      setIsDrawing(true);
    };

    const draw = (
      e:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>
    ) => {
      if (!isDrawing) return;
      e.preventDefault();

      const coords = getCoordinates(e);
      if (!coords) return;
      const { x, y } = coords;
      if (!ctx) return;
      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const stopDrawing = () => {
      if (isDrawing && ctx) {
        ctx.closePath();
        setIsDrawing(false);
      }
    };

    const clearCanvas = () => {
      const canvas = canvasRef.current;
      if (!canvas || !ctx) return;
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setResult(null);
    };

    const predict = async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      setLoading(true);

      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const formData = new FormData();
        formData.append("image", blob, "drawing.png");

        try {
          const response = await fetch(`${API_BASE_URL}/predict-image`, {
            method: "POST",
            body: formData,
          });

          const data = await response.json();

          console.log({ data });

          if (data.error) {
            alert("Error: " + data.error);
          } else {
            setResult(data);
          }
        } catch (error) {
          alert(
            "Request failed, please ensure the backend service is running!"
          );
          console.error(error);
        } finally {
          setLoading(false);
        }
      });
    };

    return (
      <div className="digitalRecognitionPage">
        <Navbar />
        <div className="drawingCanvasWrapper">
          <motion.div
            className="canvasCard"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="canvasTitle">✍️ Handwritten Digit Recognition</h2>
            <p className="canvasSubtitle">Draw a digit (0-9) on the canvas</p>

            <div className="canvasContainer">
              <canvas
                ref={canvasRef}
                width={280}
                height={280}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>

            <div className="buttonGroup">
              <motion.button
                onClick={clearCanvas}
                className="btn btnClear"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                🗑️ Clear
              </motion.button>
              <motion.button
                onClick={predict}
                className="btn btnPredict"
                disabled={loading}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {loading ? "Recognizing..." : "🔍 Recognize"}
              </motion.button>
            </div>

            {result && (
              <motion.div
                className="resultCard"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className="resultDigit"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 200,
                    damping: 10,
                    delay: 0.1,
                  }}
                >
                  {result.digit}
                </motion.div>
                <div className="resultInfo">
                  <p>
                    Confidence:{" "}
                    <strong>{(result.confidence * 100).toFixed(2)}%</strong>
                  </p>
                  <div className="probabilities">
                    <p className="probTitle">All Prediction Probabilities:</p>
                    {result.probabilities.map((prob, idx) => (
                      <div key={idx} className="probBar">
                        <span className="probLabel">{idx}</span>
                        <div className="probTrack">
                          <motion.div
                            className="probFill"
                            initial={{ width: 0 }}
                            animate={{ width: `${prob * 100}%` }}
                            transition={{ duration: 0.5, delay: idx * 0.05 }}
                          />
                        </div>
                        <span className="probValue">
                          {(prob * 100).toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return <DrawingCanvas />;
}

export default DigitalRecongnization;
